import React from 'react';
import { indexBy } from '../../../../../CodesForFoundation';
import { defineCommand } from '../../../../../concepts/commands/CommandDefinition';
import { CommandDispatcher } from '../../../../../concepts/commands/CommandDispatcher';
import { ObjectManager } from '../../../../../concepts/objects';
import { IsolatedObjectManager } from '../../../../../concepts/objects/IsolatedObjectManager';
import { Rect } from '../../../../../platform/geom/Rect';
import { Button, Colors } from '../../../../../ui';
import { Badge } from '../../../../../ui/Badge';
import { resolveSubjects } from '../../../../canvas/commands/CommandUtil';
import { Physical, RelativeTo } from '../../../../canvas/entities/Physical';
import { ContentInjector } from '../../../../canvas/injection/ContentInjector';
import { DeqoData } from '../../../../canvas/injection/DeqoData';
import { useResolve } from '../../../../Container';
import { Copy, Flexer } from '../../../../dashboard/elements/LayoutElements';
import { FlexLayout } from '../../../autoLayout/FlexLayout';
import { LensReflector } from '../../../lens/LensReflector';
import { Zone } from '../../../zones/Zone';
import { useAssitantToolCallResult } from '../../AssistantHooks';
import { AssitantToolSupport } from '../../AssistantToolSupport';
import { ToolCallPart } from '../../ChatTypes';

const groupObjectsIntoZones = defineCommand(
    'groupObjectsIntoZones',
    null,
    (cc, [spec]: [any[]]) => {

        const objects = cc.resolve(ObjectManager);
        const injector = cc.resolve(ContentInjector);

        const ids = spec.flatMap(x => x.items);
        const subjects = ids.map(x => objects.get(x, Physical));

        const iom = new IsolatedObjectManager();
        const fakes = iom.activateAll(subjects.map(x => x.toTuple()));
        const fakeLookup = new Map(subjects.map((x, i) => [x.id, fakes[i]]));

        const zones = [] as Zone[];

        // Create the groups
        for (const gp of spec) {

            const items = gp.items.map(x => fakeLookup.get(x));
            const zone = iom.create(Zone);
            zone.title = gp.label;
            zone.width = 1000;
            zone.height = 1500;

            const zoneArea = zone.layout.content.offset(zone.position(RelativeTo.Model));
            FlexLayout.exec(zoneArea, ['row', 'start', 'start', 10], items);
            for (const tm of items) {
                zone.add(tm);
            }

            zones.push(zone);
        }

        const area = new Rect(0, 0, 100000, 10000);
        FlexLayout.exec(area, ['row', 'start', 'start', 40], zones);

        const data = DeqoData.wrap(zones.map(x => x.toTuple()));
        injector.injectNative(data, { focus: 'animate', space: 200, place: true });

        return { ok: true, message: `Added ${zones.length} zones to the board.` };
    }
);

const GroupObjectsToolCallView = React.memo(({ toolCallId, args }: ToolCallPart) => {

    const dispatch = useResolve(CommandDispatcher);

    const { containerType, groups } = args;
    const result = useAssitantToolCallResult(toolCallId) as any;

    const onCopyAndGroup = () => {
        dispatch(groupObjectsIntoZones, [groups]);
    };

    return <Flexer padding=".5">
        {!result && <>
            <Flexer flex="row" align="center" gap=".5">
                <Badge color={Colors.positive}>Group By {containerType}</Badge>
            </Flexer>
            <Flexer height=".5" />
            <Copy flush>
                <p>I've sorted the objects into the following groups:</p>
                <ul>
                    {groups.map((g, i) => (
                        <li key={g.label + i}>{g.label} ({g.items.length})</li>
                    ))}
                </ul>
                <br />
                <p>Would you like to apply these groups to the board?</p>
            </Copy>
            <Flexer height=".5" />
            <Flexer flex="row" gap=".5" align="center">
                <Button onAction={onCopyAndGroup}>
                    Group
                </Button>
                <Button onAction={onCopyAndGroup}>
                    Copy &amp; Group
                </Button>
                <Button>
                    Cancel
                </Button>
            </Flexer>
        </>}
        {!!result && <>
            <Copy flush>
                {result?.message ?? 'Grouped some stuff.'}
            </Copy>
        </>}
    </Flexer>;
});

export const GroupObjectsToolSupport = AssitantToolSupport({
    toolName: 'groupObjects',
    ui: GroupObjectsToolCallView,
});

export const developerReflect = defineCommand(
    'developerReflect',
    'Developer: Reflect',
    (cc, targetIds?: string[]) => {

        const reflector = cc.resolve(LensReflector);
        const subjects = resolveSubjects(cc, targetIds);

        const aspects = reflector.reflect(subjects);
        for (const x of aspects) {
            console.log(JSON.stringify(x, null, 2));
        }
    }
);

export const groupIntoZones = defineCommand(
    'groupIntoZones',
    'Group Into Zones',
    (cc, input: { property: string, targetIds?: string[] }) => {

        const injector = cc.resolve(ContentInjector);
        const reflector = cc.resolve(LensReflector);

        const subjects = resolveSubjects(cc, input.targetIds);
        const aspect = reflector.find(subjects, input.property);

        if (!aspect) {
            throw new Error(`Aspect ${input.property} not found.`);
        }

        const zones = [] as Zone[];
        const groups = indexBy(subjects, x => `${aspect.read(x) as any}`);

        const iom = new IsolatedObjectManager();
        const fakes = iom.copyFrom(subjects);
        const subjectToFake = new Map(subjects.map((x, i) => [x, fakes[i]]));

        for (const [key, group] of groups) {

            const items = Array.from(group).map(x => subjectToFake.get(x));
            FlexLayout.portrait(['row', 'start', 'start', 10], items);

            const zone = iom.create(Zone);
            zone.title = key;
            zone.wrap(items);
            zones.push(zone);
        }

        FlexLayout.row(40, zones);

        const data = DeqoData.wrap(zones.map(x => x.toTuple()));
        injector.injectNative(data, { focus: 'animate', space: 200, place: true });

        return { ok: true, message: `Added ${zones.length} zones to the board.` };
    },
    [
        // promptForString('Property', 'property'),
    ]
);
